// V1.5 Neutral camp tuning. Mid-relay PvE per docs/02_GAME_DESIGN.md §7 and
// docs/03_MVP_SCOPE.md V1.5 IN list.
//
//   - Initial camp at sim time 0 (tier 1: 1 turret + 2 patrolling light squads).
//   - Does not respawn after being cleared.
//
// The numbers below are the only place camp tuning lives — CampSystem and
// CombatSystem read them. Change here, not at the call site (Rule 12).

export const CAMP_INITIAL_SPAWN_AT_SEC = 0;
// Legacy export retained for snapshot/test compatibility. CampSystem no
// longer schedules a reinforced respawn at this time.
export const CAMP_RESPAWN_AT_SEC = 240;

// Aggro radius: when a player squad enters this distance from a patrolling
// unit, the unit aggroes and pursues. The leash is larger so a squad that
// tries to disengage doesn't immediately drop aggro.
export const CAMP_AGGRO_RADIUS_PX = 220;
export const CAMP_LEASH_RADIUS_PX = 380;

// Patrol ring: each patrol unit walks a small clockwise loop around the camp
// anchor. Two waypoints opposite each other (180°) keep the loop simple while
// still giving visible motion.
export const CAMP_PATROL_RADIUS_PX = 110;

// Tier 1 (initial camp at match start)
export const CAMP_TIER1_PATROL_COUNT = 2;
export const CAMP_TIER1_PATROL_HP = 220;
export const CAMP_TIER1_PATROL_DPS = 16;
export const CAMP_TIER1_PATROL_RANGE_PX = 220;
export const CAMP_TIER1_PATROL_SPEED_PX_PER_SEC = 70;
export const CAMP_TIER1_TURRET_HP = 320;
export const CAMP_TIER1_TURRET_DPS = 22;
// Matches Assault's firing range (280) so the longest-range player squad
// can engage camp patrols from outside the turret's reach — gives Assault a
// real geometric role at mid instead of "you take turret fire no matter
// what." Shield/Engineer/Recon (ranges 220/220/250) still walk into turret
// fire when they close, so the turret remains a meaningful threat.
export const CAMP_TIER1_TURRET_RANGE_PX = 280;
export const CAMP_TIER1_CACHE_SIGNAL = 200;

// Legacy tier-2 tuning retained for spawnCampForTest and old telemetry shapes.
export const CAMP_TIER2_PATROL_COUNT = 3;
export const CAMP_TIER2_PATROL_HP = 320;
export const CAMP_TIER2_PATROL_DPS = 22;
export const CAMP_TIER2_PATROL_RANGE_PX = 240;
export const CAMP_TIER2_PATROL_SPEED_PX_PER_SEC = 80;
export const CAMP_TIER2_TURRET_HP = 460;
export const CAMP_TIER2_TURRET_DPS = 28;
export const CAMP_TIER2_TURRET_RANGE_PX = 380;
export const CAMP_TIER2_CACHE_SIGNAL = 400;

// Common projectile speed for camp units. Same as Recon's projectile
// (squads.ts) so the visual cadence reads naturally.
export const CAMP_PROJECTILE_SPEED = 600;
// Common vision footprint for camp units (used only for HUD reveals — camps
// don't grant vision to players).
export const CAMP_UNIT_VISION_TILES = 5;

// Cache pickup: any player squad within this radius of the cache collects.
export const CACHE_PICKUP_RADIUS_PX = 90;

// attackCooldownSec derived: shotsPerSec = dps / damagePerShot.
// We keep damagePerShot tunable separately so the cadence can be adjusted
// without re-deriving the headline DPS number.
export const CAMP_TIER1_PATROL_DAMAGE_PER_SHOT = 8;
export const CAMP_TIER1_TURRET_DAMAGE_PER_SHOT = 11;
export const CAMP_TIER2_PATROL_DAMAGE_PER_SHOT = 11;
export const CAMP_TIER2_TURRET_DAMAGE_PER_SHOT = 14;

export interface CampUnitDef {
  hp: number;
  dps: number;
  range: number;
  damagePerShot: number;
  speed: number;
  attackCooldownSec: number;
  projectileSpeed: number;
}

function tunedCooldown(dps: number, damagePerShot: number): number {
  if (dps <= 0 || damagePerShot <= 0) return 1;
  return damagePerShot / dps;
}

// Computed defs — the CampSystem reads these when minting units.
export const CAMP_DEFS = Object.freeze({
  tier1: Object.freeze({
    patrol: Object.freeze<CampUnitDef>({
      hp: CAMP_TIER1_PATROL_HP,
      dps: CAMP_TIER1_PATROL_DPS,
      range: CAMP_TIER1_PATROL_RANGE_PX,
      damagePerShot: CAMP_TIER1_PATROL_DAMAGE_PER_SHOT,
      speed: CAMP_TIER1_PATROL_SPEED_PX_PER_SEC,
      attackCooldownSec: tunedCooldown(CAMP_TIER1_PATROL_DPS, CAMP_TIER1_PATROL_DAMAGE_PER_SHOT),
      projectileSpeed: CAMP_PROJECTILE_SPEED,
    }),
    turret: Object.freeze<CampUnitDef>({
      hp: CAMP_TIER1_TURRET_HP,
      dps: CAMP_TIER1_TURRET_DPS,
      range: CAMP_TIER1_TURRET_RANGE_PX,
      damagePerShot: CAMP_TIER1_TURRET_DAMAGE_PER_SHOT,
      speed: 0,
      attackCooldownSec: tunedCooldown(CAMP_TIER1_TURRET_DPS, CAMP_TIER1_TURRET_DAMAGE_PER_SHOT),
      projectileSpeed: CAMP_PROJECTILE_SPEED,
    }),
    cacheSignal: CAMP_TIER1_CACHE_SIGNAL,
    patrolCount: CAMP_TIER1_PATROL_COUNT,
  }),
  tier2: Object.freeze({
    patrol: Object.freeze<CampUnitDef>({
      hp: CAMP_TIER2_PATROL_HP,
      dps: CAMP_TIER2_PATROL_DPS,
      range: CAMP_TIER2_PATROL_RANGE_PX,
      damagePerShot: CAMP_TIER2_PATROL_DAMAGE_PER_SHOT,
      speed: CAMP_TIER2_PATROL_SPEED_PX_PER_SEC,
      attackCooldownSec: tunedCooldown(CAMP_TIER2_PATROL_DPS, CAMP_TIER2_PATROL_DAMAGE_PER_SHOT),
      projectileSpeed: CAMP_PROJECTILE_SPEED,
    }),
    turret: Object.freeze<CampUnitDef>({
      hp: CAMP_TIER2_TURRET_HP,
      dps: CAMP_TIER2_TURRET_DPS,
      range: CAMP_TIER2_TURRET_RANGE_PX,
      damagePerShot: CAMP_TIER2_TURRET_DAMAGE_PER_SHOT,
      speed: 0,
      attackCooldownSec: tunedCooldown(CAMP_TIER2_TURRET_DPS, CAMP_TIER2_TURRET_DAMAGE_PER_SHOT),
      projectileSpeed: CAMP_PROJECTILE_SPEED,
    }),
    cacheSignal: CAMP_TIER2_CACHE_SIGNAL,
    patrolCount: CAMP_TIER2_PATROL_COUNT,
  }),
});
